import { Dispatch, SetStateAction } from "react";
import { CommentDto, Country, CountryProgress } from "../../../../api/Api";
import { LifecycleName } from "../../../../auth/roles";
import { ownsLifecycle } from "../../../../auth/permissions";
import { useRoles } from "../../../../auth/useRoles";
import { useLifecycleComments } from "../../../../components/lifecycle/useLifecycleComments";
import { createLifecycleActions } from "../../../../components/lifecycle/createLifecycleActions";
import { buildProgressOverride } from "../../../../components/lifecycle/progressOverride/buildProgressOverride";
import { useUpdateCountry } from "../../dataHooks/countryQueries";

export type CountryLifecycleKey =
  | "marketAccessLifecycle"
  | "complianceLifecycle"
  | "legalLifecycle"
  | "riskLifecycle"
  | "creditRiskLifecycle"
  | "treasuryLifecycle"
  | "financeLifecycle"
  | "techLifecycle";

const PROGRESS_KEYS: Record<CountryLifecycleKey, keyof CountryProgress> = {
  marketAccessLifecycle: "marketAccess",
  complianceLifecycle: "compliance",
  legalLifecycle: "legal",
  riskLifecycle: "risk",
  creditRiskLifecycle: "creditRisk",
  treasuryLifecycle: "treasury",
  financeLifecycle: "finance",
  techLifecycle: "tech"
};

/** Which department's role owns each lifecycle, for the progress rule below. */
const LIFECYCLE_NAMES: Record<CountryLifecycleKey, LifecycleName> = {
  marketAccessLifecycle: "marketaccess",
  complianceLifecycle: "compliance",
  legalLifecycle: "legal",
  riskLifecycle: "risk",
  creditRiskLifecycle: "creditrisk",
  treasuryLifecycle: "treasury",
  financeLifecycle: "finance",
  techLifecycle: "tech"
};

type Args<TForm extends { comments: CommentDto[] }> = {
  country: Country;
  lifecycle: CountryLifecycleKey;
  formData: TForm;
  setFormData: Dispatch<SetStateAction<TForm>>;
  onClose: () => void;
};

/**
 * Everything the seven country lifecycle dialogs share: building the document to save, moving the
 * progress circle, and the comment thread.
 *
 * `buildCountry` below is the **only** place in the country lifecycle dialogs that
 * spreads the whole stored entity into a save payload. That spread is the lost-update bug described
 * in the backend's docs/lifecycle-patch-endpoints.md, and countries have no slice PATCH endpoints
 * yet. When they arrive, this file changes and the dialogs do not.
 */
export const useCountryLifecycle = <TForm extends { comments: CommentDto[] }>({
  country,
  lifecycle,
  formData,
  setFormData,
  onClose
}: Args<TForm>) => {
  const roles = useRoles();
  const update = useUpdateCountry({ onSuccess: onClose });
  const commentUpdate = useUpdateCountry();
  // Its own mutation, so the admin progress dropdown never closes the dialog.
  const progressUpdate = useUpdateCountry();

  const progressKey = PROGRESS_KEYS[lifecycle];

  // Only the owning department starts a lifecycle. Market Access and Admin open every dialog to
  // assign whoever will do the work, and that assignment is not the work beginning: moving the
  // circle then would tell the overview that a department has picked something up when it has not.
  const canSetInProgress = ownsLifecycle(roles, LIFECYCLE_NAMES[lifecycle]);

  /**
   * The stored country with this dialog's slice written over it. `extraSlice` carries fields the
   * dialog owns but does not hold in formData (the party row collections, mostly). `extraDocument`
   * carries whole slices outside this dialog's own, which only the warehouse sync needs: a country's
   * credit values live on Credit Risk while the Catalyst ID that identifies them is on Compliance,
   * so accepting a synced credit value from the Compliance dialog has to write the credit risk slice
   * or the value lands where nothing reads it and is gone on the next reload. The counterparty
   * compliance dialog does the same thing for the same reason.
   */
  const buildCountry = (
    comments: CommentDto[],
    extraSlice?: Record<string, unknown>,
    extraDocument?: Partial<Country>
  ): Country => {
    // The comment thread is passed in separately, so it must not ride along inside the slice.
    const slice = Object.fromEntries(
      Object.entries(formData).filter(([field]) => field !== "comments")
    );

    return {
      ...country,
      ...extraDocument,
      [lifecycle]: {
        ...country[lifecycle],
        ...slice,
        ...extraSlice,
        comments
      }
    } as Country;
  };

  const comments = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (updated) => buildCountry(updated)
  });

  // Party rows are supplied at click time. Merge them before the shared action adds
  // pending comments and completion, so they cannot overwrite either of those.
  const actionsFor = (
    extraSlice?: Record<string, unknown>,
    extraDocument?: Partial<Country>
  ) =>
    createLifecycleActions({
      formData: { ...formData, ...extraSlice, comments: formData.comments },
      currentComment: comments.currentComment,
      currentProgress: country.progress?.[progressKey],
      canSetInProgress,
      buildEntity: (values, progress) => {
        const { comments: updatedComments, ...slice } = values;
        return {
          ...buildCountry(updatedComments, slice, extraDocument),
          progress: {
            ...country.progress,
            // Re-merged because the line above rebuilds the whole progress object from the stored
            // country, which would otherwise drop whatever extraDocument set (the sync moving Credit
            // Risk's circle, say). This dialog's own circle is still the action's to set, so it goes last.
            ...extraDocument?.progress,
            [progressKey]: progress
          }
        };
      },
      mutate: update.mutate
    });

  const handleSave = (
    extraSlice?: Record<string, unknown>,
    extraDocument?: Partial<Country>
  ) => actionsFor(extraSlice, extraDocument).handleSave();

  const handleComplete = (
    extraSlice?: Record<string, unknown>,
    extraDocument?: Partial<Country>
  ) => actionsFor(extraSlice, extraDocument).handleComplete();

  return {
    ...comments,
    buildCountry,
    handleSave,
    handleComplete,
    progressOverride: buildProgressOverride(
      country,
      progressKey,
      progressUpdate
    ),
    // Either write blocks the dialog: a save in flight, or a comment being persisted on its own.
    isSaving: update.isPending || comments.isSaving
  };
};
